import { request } from './api/client'

/* ============================================================================
   session.ts — WHO IS USING THIS.

   TQ-01 — the token verification approach — is still unanswered, so there is still no
   real token. What changed in Sprint 7 is that there is now a real SESSION: a person
   is chosen, the server looks them up, and everything after that point is genuine —
   the property scope, the roles, and the three-state permission grants all come from
   the database for that user.

   So the weak part is one sentence, and it is precisely the sentence TQ-01 answers:
   "and we believe you are who you say you are". Everything else — every authorisation
   decision every screen makes — is the code that will ship.

   The session is held in memory. A reload signs you out, which is correct for a
   preview: there is nothing to remember that would be safe to remember, and a
   "remembered" identity with no verification behind it is an invitation.
   ========================================================================= */

export type PermissionGrant = 'DENIED' | 'ALLOWED' | 'ALLOWED_WITH_REASON'

export interface SessionProperty {
  propertyId: string
  code: string
  name: string
  /** The hotel's own name in Arabic, or null until somebody types one (CQ-130).
   *
   *  It sits in the sidebar above every screen, and was drawn in Latin script on every
   *  Arabic one from Sprint 2 to Sprint 30 — chrome rather than content, which is
   *  exactly why nobody's eye caught it. `propertyName(p, locale)` is how to read it. */
  nameAr: string | null
  defaultLanguage: string
}

/** The hotel's name in the reader's language, falling back to the English rather than
 *  to a blank: an untranslated hotel should read as untranslated. */
export const propertyName = (property: SessionProperty, locale: 'en' | 'ar') =>
  locale === 'ar' ? property.nameAr ?? property.name : property.name

export interface Session {
  /** The signed token this session runs on. Sent as `Authorization: Bearer` on every
   *  request; never written to storage — see the note at the top of this file. */
  token: string
  expiresAtUtc: string
  userId: string
  employeeId: string
  displayName: string
  displayNameAr: string | null
  preferredLanguage: string
  roles: string[]
  properties: SessionProperty[]
  /** Keyed by permission code. Absent means denied. */
  permissions: Record<string, PermissionGrant>
}

let current: Session | null = null

/**
 * **WHICH OF THE PERSON'S PROPERTIES THEY ARE LOOKING AT.** Sprint 48.
 *
 * Null means "the first one", which is what every screen has meant since Sprint 7 and is
 * still right for the overwhelming majority of people: a housekeeper works at one hotel.
 * It is a separate variable rather than a field on the session because it is not
 * something the server told us — it is where this person is standing right now, and it
 * must not survive a sign-out into somebody else's session.
 */
let chosenPropertyId: string | null = null

export function currentSession(): Session {
  if (!current)
    throw new Error(
      'No session. A screen read the session before anyone signed in — the application '
      + 'shell must not render its screens until signIn() has resolved.')
  return current
}

/** Null rather than throwing, for the shell deciding whether to show sign-in. */
export function sessionOrNull(): Session | null {
  return current
}

export function clearSession(): void {
  current = null
  chosenPropertyId = null
}

/** For tests, and for the sign-in screen once it has a session in hand. */
export function setSession(session: Session): void {
  current = session
  /* **A NEW SESSION FORGETS WHICH HOTEL THE LAST ONE WAS LOOKING AT.** Two people share
     a handset at a shift change; carrying Karbala over into the Najaf supervisor's
     session would put him on the wrong hotel's board without his having chosen it, and
     the board would look plausible. */
  chosenPropertyId = null
}

/**
 * THE HEADER THE CLIENT SENDS — a real bearer token since 9 September 2026.
 *
 * It used to be the user id in clear, which was the honest arrangement while the front
 * door was a preview stub that accepted any employee number: a signed header would have
 * been a lock on a door with no wall. The staging deployment now runs as an independent
 * application with its own token, so the token is real, signed, and expires.
 *
 * `Authorization` and not a custom header, so that a proxy, a log scrubber or an IIS
 * module treats it as the secret it is rather than as an interesting string to log.
 */
export const SESSION_HEADER = 'Authorization'

/**
 * The staging site's shared access key. NOT a password and not per person — see
 * `StagingGateMiddleware`. It is needed only at sign-in, and only on a deployment that
 * has one, which is why `signIn` takes it as an optional argument rather than the
 * application carrying it about.
 */
export const STAGING_KEY_HEADER = 'X-Housekeeping-Staging-Key'

export function sessionHeaders(): Record<string, string> {
  return current ? { [SESSION_HEADER]: `Bearer ${current.token}` } : {}
}

/**
 * Has the session run out, by our own clock?
 *
 * The server decides authoritatively and answers `auth.sessionExpired`; this is so the
 * application can stop making requests it knows will fail — a handset in a stairwell
 * with an expired token should say "sign in again", not queue a morning's work behind a
 * request that can never succeed.
 */
export function sessionHasExpired(now: Date = new Date()): boolean {
  return current !== null && new Date(current.expiresAtUtc).getTime() <= now.getTime()
}

/* ── the endpoints ────────────────────────────────────────────────────────── */

export interface TestUser {
  userId: string
  employeeId: string
  displayName: string
  displayNameAr: string | null
  preferredLanguage: string
  roleCode: string
  roleName: string
}

/**
 * The people who can be signed in as, for the picker.
 *
 * PREVIEW ONLY. The server returns 404 when a real identity provider is bound, because
 * a list of everyone who can sign in is not something a real system publishes — so an
 * empty result here is the correct, expected answer in production and must render as
 * "type your employee number", not as an error.
 */
export async function fetchTestUsers(): Promise<TestUser[]> {
  try {
    return await request<TestUser[]>('/auth/test-users')
  } catch {
    return []
  }
}

/**
 * Exchange an assertion for a TOKEN, then load what that identity may do.
 *
 * `stagingKey` is the shared site key on a staging deployment, and undefined everywhere
 * else. It is passed straight through to this one request and never stored: a key kept
 * in memory for the life of the application is a key that ends up in a crash report.
 */
export async function signIn(assertion: string, stagingKey?: string): Promise<Session> {
  const identity = await request<{
    token: string
    expiresAtUtc: string
    userId: string
  }>('/auth/sign-in', {
    method: 'POST',
    body: { assertion },
    ...(stagingKey ? { headers: { [STAGING_KEY_HEADER]: stagingKey } } : {}),
  })

  // Set before the second call, because /auth/me is read AS that user — and it is read
  // with the TOKEN, so this assignment is what authorises it.
  current = {
    token: identity.token,
    expiresAtUtc: identity.expiresAtUtc,
    userId: identity.userId,
  } as Session

  try {
    const me = await request<{
      userId: string
      employeeId: string
      displayName: string
      displayNameAr: string | null
      preferredLanguage: string
      roles: string[]
      properties: SessionProperty[]
      permissions: Record<string, string>
    }>('/auth/me')

    current = {
      ...me,
      token: identity.token,
      expiresAtUtc: identity.expiresAtUtc,
      /* THE GRANT ARRIVES AS ITS WIRE NAME, and is checked rather than trusted.

         It arrived as a NUMBER until 9 September 2026 — 0, 1, 2 — and was mapped here.
         That was the one enum in the product travelling as an integer, and the mapping
         was the only thing standing between the server's numbering and every permission
         check on three clients: renumber the enum on the server, and `Allowed` silently
         becomes `Denied` with nothing failing anywhere. The server now spells it, like
         every other enum it sends.

         ANYTHING UNRECOGNISED IS DENIED, which is the safe direction and the only one:
         a client that cannot understand a grant must not act on it. */
      permissions: Object.fromEntries(
        Object.entries(me.permissions).map(([code, value]): [string, PermissionGrant] =>
          [code, GRANTS.includes(value as PermissionGrant) ? (value as PermissionGrant) : 'DENIED'])),
    }
    return current
  } catch (error) {
    current = null
    throw error
  }
}

const GRANTS: readonly PermissionGrant[] = ['DENIED', 'ALLOWED', 'ALLOWED_WITH_REASON']

/* ── asking about permissions ─────────────────────────────────────────────── */

/**
 * May this person do this at all?
 *
 * TRUE FOR BOTH ALLOWED STATES, deliberately. The three-state grant exists so that a
 * legitimate-but-never-routine action stays possible; a screen that hides the button
 * for AllowedWithReason has turned the third state back into "denied" and the
 * supervisor works around the system on the day it matters.
 */
export function may(permission: string): boolean {
  return (current?.permissions[permission] ?? 'DENIED') !== 'DENIED'
}

/**
 * May this person do ANYTHING in a family of permissions?
 *
 * WHAT IT IS FOR: the sidebar. Reporting is one screen and eight report definitions,
 * and the shell must not know a single report code — the day a ninth is added, the
 * sidebar would be one edit away from hiding it from the people who may run it, and
 * nothing would fail. The screen itself is filtered by the SERVER, which is the only
 * place that knows which reports exist; this decides only whether the door is drawn.
 *
 * The prefix is a permission FAMILY and must end in a dot. `report` without one would
 * match a permission called `reporting.admin` that nobody intended.
 */
export function mayAny(prefix: string): boolean {
  if (!prefix.endsWith('.'))
    throw new Error(`mayAny expects a permission family ending in a dot, not "${prefix}".`)
  return Object.entries(current?.permissions ?? {})
    .some(([code, grant]) => code.startsWith(prefix) && grant !== 'DENIED')
}

/**
 * Must this person type a reason first?
 *
 * The screen asks BEFORE acting, so the reason box appears with the action rather than
 * after a refusal comes back from the server. The server checks it again regardless —
 * this is about not wasting somebody's time, not about enforcement.
 */
export function needsReason(permission: string): boolean {
  return current?.permissions[permission] === 'ALLOWED_WITH_REASON'
}

/**
 * **WHICH HOTEL EVERY SCREEN IS ABOUT.**
 *
 * The person's chosen one where they have chosen, and the first otherwise. Faiz e
 * Husaini is Najaf and Karbala, and the client's answer on 17 September was that the two
 * are COMPLETELY SEPARATE — separate staff, separate stores, separate boards, nothing
 * shared. So this is not a filter: it is which hotel you are in, and every screen in the
 * product is about one of them at a time.
 *
 * **THE CHOICE IS CHECKED AGAINST THE SESSION EVERY TIME**, rather than trusted once
 * when it was made. A property the person no longer holds a role at — a transfer, a role
 * withdrawn while they had the tab open — falls back to the first rather than sending
 * requests the server will refuse, or, worse, quietly naming a hotel in the sidebar that
 * this person is no longer at.
 */
export function currentPropertyId(): string {
  const properties = currentSession().properties
  if (properties.length === 0)
    throw new Error(
      'This user holds no role at any property. That is the NEW JOINER GAP (TQ-17): '
      + 'they signed in successfully and an administrator has not assigned them a role.')

  const chosen = properties.find((p) => p.propertyId === chosenPropertyId)
  return (chosen ?? properties[0]!).propertyId
}

/** The whole record, for a sidebar that has to name the hotel it is drawing. */
export function currentProperty(): SessionProperty {
  const id = currentPropertyId()
  return currentSession().properties.find((p) => p.propertyId === id)!
}

/**
 * **CHANGE HOTEL.** Refused for a property this person holds no role at — a picker that
 * accepted any id would be an authorisation decision taken by a dropdown.
 *
 * Everything on screen belongs to the hotel that was showing when it was drawn, so the
 * shell REMOUNTS its screens after this rather than letting them keep their state: a
 * board still holding Najaf's rooms under Karbala's name is worse than a moment's blank.
 */
export function setCurrentProperty(propertyId: string): void {
  const known = currentSession().properties.some((p) => p.propertyId === propertyId)
  if (!known)
    throw new Error(
      `This user holds no role at property ${propertyId}, so it cannot be selected.`)
  chosenPropertyId = propertyId
}
